import { Command } from "commander";
import { logErr, logOk, S } from "../helpers.ts";
import {
  clearStoreTokens,
  getStoreConfig,
  isStoreAuthenticated,
  saveStoreTokens,
  storeFetch,
} from "../store-client.ts";

/**
 * Creates the `skaile store` command group.
 *
 * Manages authentication and publishing operations against the AI Asset Store.
 * Subcommands: `login` (GitHub OAuth or email/password), `logout`, `whoami`,
 * `publish` (push a new asset version), `yank` (soft-delete a version).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/store#make-store-command
 */
export function makeStoreCommand(): Command {
  const store = new Command("store").description("Manage the AI Asset Store");

  // ── login ──
  store
    .command("login")
    .description("Authenticate with the store")
    .option("--email <email>", "Login with email instead of GitHub")
    .action(async (opts) => {
      const config = getStoreConfig();

      if (opts.email) {
        // Email/password login
        const readline = await import("node:readline");
        const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
        const password = await new Promise<string>((resolve) => {
          rl.question("Password: ", (answer) => {
            rl.close();
            resolve(answer);
          });
        });

        try {
          const res = await storeFetch<{ jwt: string; refreshToken: string; publisher: any }>(
            config,
            "/api/auth/login",
            { method: "POST", body: { email: opts.email, password } },
          );
          saveStoreTokens(res.jwt, res.refreshToken);
          logOk(`Logged in as ${S.brand(`@${res.publisher.slug}`)}`);
        } catch (e: any) {
          logErr(e.message);
          process.exit(1);
        }
      } else {
        // GitHub OAuth — open browser
        const loginUrl = `${config.url}/api/auth/login/github`;
        console.log(`\n  Opening browser to authenticate...\n  ${S.dim(loginUrl)}\n`);

        // Start a local server to receive the callback
        const http = await import("node:http");
        const { URL } = await import("node:url");

        const server = http.createServer((req, res) => {
          const url = new URL(req.url ?? "/", "http://localhost");
          const jwt = url.searchParams.get("token");
          const refresh = url.searchParams.get("refresh");

          if (jwt && refresh) {
            saveStoreTokens(jwt, refresh);
            res.writeHead(200, { "Content-Type": "text/html" });
            res.end("<h2>Authenticated! You can close this tab.</h2>");
            logOk("Authenticated with the store");
            server.close();
          } else {
            res.writeHead(400);
            res.end("Missing tokens");
          }
        });

        server.listen(0, () => {
          const port = (server.address() as any)?.port;
          const open =
            process.platform === "darwin"
              ? "open"
              : process.platform === "win32"
                ? "start"
                : "xdg-open";
          import("node:child_process").then(({ exec }) => {
            exec(`${open} "${loginUrl}"`);
          });
          console.log(`  ${S.dim(`Listening on http://localhost:${port} for callback...`)}`);
        });

        // Timeout after 2 minutes
        setTimeout(() => {
          logErr("Login timed out");
          server.close();
          process.exit(1);
        }, 120_000);
      }
    });

  // ── logout ──
  store
    .command("logout")
    .description("Sign out of the store")
    .action(async () => {
      const config = getStoreConfig();
      if (config.refreshToken) {
        try {
          await storeFetch(config, "/api/auth/logout", {
            method: "POST",
            body: { refreshToken: config.refreshToken },
          });
        } catch {
          // ignore — clear locally regardless
        }
      }
      clearStoreTokens();
      logOk("Logged out");
    });

  // ── whoami ──
  store
    .command("whoami")
    .description("Show current store identity")
    .action(async () => {
      const config = getStoreConfig();
      if (!isStoreAuthenticated(config)) {
        logErr("Not authenticated. Run: skaile store login");
        process.exit(1);
      }

      try {
        const me = await storeFetch<any>(config, "/api/auth/me");
        console.log();
        console.log(`  ${S.heading("Publisher")}  ${S.brand(`@${me.slug}`)}`);
        console.log(`  ${S.label("Name")}       ${me.displayName}`);
        console.log(`  ${S.label("Email")}      ${me.email}`);
        console.log(`  ${S.label("Verified")}   ${me.verified ? S.ok("yes") : S.dim("no")}`);
        console.log();
      } catch (e: any) {
        logErr(e.message);
        process.exit(1);
      }
    });

  // ── mode ──
  store
    .command("mode <auto|local|remote>")
    .description("Set the catalog resolution mode (not yet wired)")
    .action((_mode: string) => {
      logErr(
        "`skaile store mode` is scaffolded but not yet wired. " +
          "Store config lives at ~/.skaile/store/config.yaml.",
      );
      process.exit(2);
    });

  // ── search ──
  store
    .command("search <query>")
    .description("Search the aggregated catalog (not yet wired)")
    .action((_query: string) => {
      logErr("`skaile store search` is scaffolded but not yet wired.");
      process.exit(2);
    });

  // ── show ──
  store
    .command("show <ref>")
    .description("Show details + provenance for a manifest (not yet wired)")
    .action((_ref: string) => {
      logErr("`skaile store show` is scaffolded but not yet wired.");
      process.exit(2);
    });

  // ── favourite / unfavourite ──
  store
    .command("favourite <ref>")
    .description("Pin a ref to your favourites (not yet wired)")
    .action((_ref: string) => {
      logErr("`skaile store favourite` is scaffolded but not yet wired.");
      process.exit(2);
    });
  store
    .command("unfavourite <ref>")
    .description("Remove a ref from your favourites (not yet wired)")
    .action((_ref: string) => {
      logErr("`skaile store unfavourite` is scaffolded but not yet wired.");
      process.exit(2);
    });

  // ── sync ──
  store
    .command("sync")
    .description("Refresh the local cache from the remote store (not yet wired)")
    .action(() => {
      logErr("`skaile store sync` is scaffolded but not yet wired.");
      process.exit(2);
    });

  // ── push ──
  store
    .command("push")
    .description("Push the local store to a git remote (not yet wired)")
    .option("--remote <name>", "Git remote name", "origin")
    .action(() => {
      logErr("`skaile store push` is scaffolded but not yet wired.");
      process.exit(2);
    });

  // ── yank ──
  store
    .command("yank <version>")
    .description("Yank (soft-delete) a published version")
    .option("--asset <ref>", "Asset reference (@publisher/slug)")
    .action(async (version: string, opts) => {
      const config = getStoreConfig();
      if (!isStoreAuthenticated(config)) {
        logErr("Not authenticated. Run: skaile store login");
        process.exit(1);
      }

      if (!opts.asset) {
        logErr("--asset is required (e.g. --asset @alice/my-skill)");
        process.exit(1);
      }

      const ref = opts.asset.replace(/^@/, "");
      const [publisher, slug] = ref.split("/");
      if (!publisher || !slug) {
        logErr("Invalid asset ref. Use format: @publisher/slug");
        process.exit(1);
      }

      try {
        await storeFetch(config, `/api/assets/${publisher}/${slug}/${version}/yank`, {
          method: "PATCH",
        });
        logOk(`Yanked v${version} of @${publisher}/${slug}`);
      } catch (e: any) {
        logErr(e.message);
        process.exit(1);
      }
    });

  return store;
}
